// src/api/ProjectManager.js
import { v4 as uuidv4 } from "uuid";
import {
  S3Client,
  ListObjectsV2Command,
  GetObjectCommand,
} from "@aws-sdk/client-s3";

import { createEntityManager, resolveVarsFromContext } from "./EntityManager";

import { loadOntology } from "./OntologyUtils";

// --- Config helpers (decoupled from GeoVisionAI.js) ---
const REGION = process.env.REACT_APP_AWS_REGION || "us-east-1";
const BUCKET_NAME = process.env.REACT_APP_AZRIUS_APP_BUCKET || "azri.us-data";

function getS3Client(awsCredentials) {
  return new S3Client({
    region: REGION,
    credentials: awsCredentials,
  });
}

// --- Name uniqueness helper ---
export function ensureUniqueName(desired, existingNames) {
  if (!existingNames.has(desired)) return desired;

  const m = desired.match(/^(.*?)(?:\s\((\d+)\))?$/);
  const base = (m?.[1] || desired).trim();
  let n = parseInt(m?.[2] || "1", 10) + 1;

  let candidate = `${base} (${n})`;
  while (existingNames.has(candidate)) {
    n += 1;
    candidate = `${base} (${n})`;
  }
  return candidate;
}

// --- Stream/Body -> string (browser & node friendly) ---
export async function bodyToString(body) {
  if (typeof body === "string") return body;
  if (body instanceof Blob) return await body.text();
  try {
    return await new Response(body).text();
  } catch {
    return await new Promise((resolve, reject) => {
      const chunks = [];
      body.on?.("data", (c) => chunks.push(Buffer.from(c)));
      body.on?.("end", () => resolve(Buffer.concat(chunks).toString("utf-8")));
      body.on?.("error", reject);
    });
  }
}

// --- Read existing names from project_*.json under prefix ---
export async function getExistingProjectNames(s3, prefix) {
  const names = new Set();
  let ContinuationToken;

  do {
    const list = await s3.send(
      new ListObjectsV2Command({
        Bucket: BUCKET_NAME,
        Prefix: prefix,
        ContinuationToken,
      }),
    );

    const keys =
      list.Contents?.map((o) => o.Key).filter((k) =>
        /project_.*\.json$/.test(k),
      ) || [];

    await Promise.allSettled(
      keys.map(async (Key) => {
        const obj = await s3.send(
          new GetObjectCommand({ Bucket: BUCKET_NAME, Key }),
        );
        const text = await bodyToString(obj.Body);
        const json = JSON.parse(text);
        if (json?.projectName) names.add(json.projectName);
      }),
    );

    ContinuationToken = list.IsTruncated
      ? list.NextContinuationToken
      : undefined;
  } while (ContinuationToken);

  return names;
}

// --- Create project using ontology + unique name guard ---
export async function createNamedProject(
  userId,
  projectName,
  formValues = {},
  awsCredentials,
) {
  if (!userId) throw new Error("userId is required");
  if (!projectName) throw new Error("projectName is required");
  if (!awsCredentials) throw new Error("awsCredentials are required");

  // 0) Compute the projects prefix used by your existing helpers
  //    (kept identical to your previous implementation so the name scan works)
  const projectsPrefix = `geovision/${userId}/living_harmony_profile/project/`;

  // 1) Ensure unique display name using your existing procedures
  const s3 = getS3Client(awsCredentials);
  const existingNames = await getExistingProjectNames(s3, projectsPrefix);
  const uniqueName = ensureUniqueName(projectName, existingNames);

  // 2) Load the Project ontology & build a manager
  const projectOntology = await loadOntology("project"); // or "Project" if your filename uses caps
  const mgr = createEntityManager(projectOntology);

  // 3) Resolve vars for templates (cover {COGNITO_ID}, {PROJECT_UUID}, {UUID}, optionally {s3Prefix})
  const projectUuid = "project_" + uuidv4();
  const vars = resolveVarsFromContext(mgr.meta, {
    userId, // -> {COGNITO_ID}
    projectUuid, // -> {PROJECT_UUID}
    UUID: projectUuid, // -> {UUID}
    s3Prefix: projectsPrefix,
  });

  // 4) Prepare the patch payload; mgr.update() will stamp created/updated times
  const { propertyId = "", address = "", townStateZip = "" } = formValues;
  const patch = {
    // Preferred display name fields
    id: projectUuid,
    projectName: uniqueName,
    name: uniqueName,
    title: uniqueName,
    status: "new",

    // Optional form fields
    propertyId,
    address,
    townStateZip,

    // Keep compatibility with prior structure if other parts of the app read these
    s3_prefix: projectsPrefix,
    assets_prefix: `${projectsPrefix}${projectUuid}/`,
  };

  // 5) Persist via ontology manager
  const saved = await mgr.update({ vars, patch, aws: awsCredentials });

  return saved;
}

/**
 * List a user's saved projects from:
 *   geovision/$COGNITO_ID/living_harmony_profile/project/
 *
 * @param {string} userId        Cognito Identity ID (e.g., us-east-1:xxxx)
 * @param {object} awsCreds      { accessKeyId, secretAccessKey, sessionToken } (from useAuthWait)
 * @returns {Promise<Array>}     Array of project objects, newest first
 */
export async function listNamedProjects(userId, awsCreds) {
  if (!BUCKET_NAME) {
    throw new Error("REACT_APP_S3_BUCKET is not set");
  }
  if (!userId) {
    return [];
  }

  // Ensure AWS SDK is configured for this call
  AWS.config.update({
    region: REGION,
    credentials: awsCreds?.accessKeyId
      ? new AWS.Credentials({
          accessKeyId: awsCreds.accessKeyId,
          secretAccessKey: awsCreds.secretAccessKey,
          sessionToken: awsCreds.sessionToken,
        })
      : AWS.config.credentials,
  });

  const s3 = new AWS.S3({ apiVersion: "2006-03-01" });
  const prefix = `geovision/${userId}/living_harmony_profile/project/`;

  // 1) List all objects under the prefix (paginate if needed)
  const keys = await listAllKeys(s3, BUCKET_NAME, prefix);

  // 2) Keep only plausible project files (JSON files)
  const jsonKeys = keys.filter(
    (k) => k.endsWith(".json") && !k.endsWith("index.json"),
  );

  if (jsonKeys.length === 0) return [];

  // 3) Fetch & parse all project JSONs in parallel (best‑effort)
  const results = await Promise.all(
    jsonKeys.map(async (Key) => {
      try {
        const obj = await s3.getObject({ Bucket: BUCKET_NAME, Key }).promise();

        const body = obj.Body?.toString("utf-8") || "{}";
        const data = safeJsonParse(body, {});

        // Normalize a few common fields
        // Sample we’ve seen:
        // {
        //   "id": "...",
        //   "propertyId": "930887698",
        //   "address": "1100 Great Road",
        //   "townStateZip": "Lincoln, RI, 02865",
        //   "created_at": "2025-08-08T19:19:45.860Z",
        //   "updated_at": "2025-08-08T19:19:45.860Z",
        //   "s3_prefix": "geovision/<id>/living_harmony_profile/project/"
        // }
        const project = {
          ...data,
          // fallback id from filename if missing
          id: data.id || keyBasename(Key).replace(/\.json$/i, ""),
          s3_prefix: data.s3_prefix || prefix,
          _s3_key: Key, // handy internal reference
        };

        return project;
      } catch (err) {
        console.warn("[ProjectManager] Failed to load project:", Key, err);
        return null;
      }
    }),
  );

  // 4) Filter out failures and sort newest first by updated_at || created_at || _lastModified
  const existing = results.filter(Boolean);

  // Fetch last-modified metadata for a stable fallback sort
  const keyedMeta = await headMetaMap(
    s3,
    BUCKET_NAME,
    existing.map((p) => p._s3_key),
  );

  const sorted = existing.sort((a, b) => {
    const at =
      Date.parse(a.updated_at || a.created_at || keyedMeta[a._s3_key] || 0) ||
      0;
    const bt =
      Date.parse(b.updated_at || b.created_at || keyedMeta[b._s3_key] || 0) ||
      0;
    return bt - at;
  });

  // 5) Strip internal fields before returning
  return sorted.map(({ _s3_key, ...rest }) => rest);
}

/* ------------------------- helpers ------------------------- */

function safeJsonParse(text, fallback) {
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

function keyBasename(key) {
  const idx = key.lastIndexOf("/");
  return idx >= 0 ? key.slice(idx + 1) : key;
}

async function listAllKeys(s3, Bucket, Prefix) {
  const keys = [];
  let ContinuationToken;

  do {
    const resp = await s3
      .listObjectsV2({
        Bucket,
        Prefix,
        ContinuationToken,
        MaxKeys: 1000,
      })
      .promise();

    (resp.Contents || []).forEach((o) => {
      // skip "folders"
      if (o.Key && !o.Key.endsWith("/")) keys.push(o.Key);
    });

    ContinuationToken = resp.IsTruncated
      ? resp.NextContinuationToken
      : undefined;
  } while (ContinuationToken);

  return keys;
}

async function headMetaMap(s3, Bucket, keys) {
  // get LastModified for each key; do it in batches to avoid spamming
  const meta = {};
  const batchSize = 10;

  for (let i = 0; i < keys.length; i += batchSize) {
    const slice = keys.slice(i, i + batchSize);
    await Promise.all(
      slice.map(async (Key) => {
        try {
          const head = await s3.headObject({ Bucket, Key }).promise();
          meta[Key] = head.LastModified?.toString?.() || null;
        } catch {
          meta[Key] = null;
        }
      }),
    );
  }
  return meta;
}
